import { useState } from 'react';
import { swatchEdgeColor, type AssetRef, type Color } from '@motorcity/shared';
import { AssetField } from './AssetField';
import { LocalizedInput } from './Fields';
import { ColorPicker } from './ColorPicker';
import { EmptyState, SectionHeader } from './ui/primitives';

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Paint and trim options for a model. Each entry pairs a name and a swatch with
 * an exterior and an interior photograph, so the app can drive both a colour
 * chip row and the gallery behind it from one list.
 */
export function ColorEditor({
  value,
  onChange,
  assetPrefix,
}: {
  value: Color[];
  onChange: (next: Color[]) => void;
  assetPrefix: string;
}) {
  const [picking, setPicking] = useState<{ index: number; asset: AssetRef } | undefined>();

  const update = (index: number, patch: Partial<Color>) =>
    onChange(value.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    // sortOrder is what the published JSON is ordered by, so rewrite it here
    // rather than relying on array position surviving a round trip.
    onChange(next.map((c, i) => ({ ...c, sortOrder: i })));
  };

  const add = () =>
    onChange([
      ...value,
      {
        id: `color-${crypto.randomUUID().slice(0, 8)}`,
        name: { en: '', ar: '' },
        swatch: '#cccccc',
        sortOrder: value.length,
      },
    ]);

  return (
    <section className="card">
      <SectionHeader
        title="Colours"
        description="One entry per paint option. Upload the exterior shot, then sample the swatch straight from it."
        action={
          <button type="button" className="btn" onClick={add}>
            Add colour
          </button>
        }
      />

      {value.length === 0 ? (
        <EmptyState
          title="No colours yet"
          body="Add a colour to drive the chip row and gallery in the app."
          action={
            <button type="button" className="btn primary" onClick={add}>
              Add colour
            </button>
          }
        />
      ) : null}

      <ul className="color-list">
        {value.map((color, index) => {
          const validHex = HEX.test(color.swatch);
          return (
            <li key={color.id} className="color-item">
              <div className="color-head">
                <span
                  className="swatch"
                  style={
                    validHex
                      ? {
                          background: color.swatch,
                          // Derived from the fill, so a white paint is still a
                          // visible chip on a white card.
                          borderColor: swatchEdgeColor(color.swatch),
                        }
                      : undefined
                  }
                  aria-hidden="true"
                />
                <strong className="color-title">
                  {color.name.en || <span className="muted">Untitled colour</span>}
                </strong>
                <div className="color-controls">
                  <button
                    type="button"
                    className="btn icon"
                    aria-label="Move up"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn icon"
                    aria-label="Move down"
                    disabled={index === value.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn icon danger"
                    aria-label={`Remove ${color.name.en || 'colour'}`}
                    onClick={() =>
                      onChange(
                        value
                          .filter((_, i) => i !== index)
                          .map((c, i) => ({ ...c, sortOrder: i })),
                      )
                    }
                  >
                    ×
                  </button>
                </div>
              </div>

              <LocalizedInput
                label="Colour name"
                required
                value={color.name}
                onChange={(name) => update(index, { name })}
              />

              <div className="field">
                <span className="field-label">Swatch</span>
                <div className="row">
                  <input
                    type="color"
                    className="input color-input"
                    value={validHex ? color.swatch : '#cccccc'}
                    onChange={(e) => update(index, { swatch: e.target.value.toLowerCase() })}
                    aria-label="Swatch colour"
                  />
                  <input
                    className="input mono"
                    value={color.swatch}
                    maxLength={7}
                    placeholder="#1a1a1a"
                    onChange={(e) => update(index, { swatch: e.target.value.trim().toLowerCase() })}
                  />
                  <button
                    type="button"
                    className="btn"
                    disabled={!color.exteriorImage}
                    title={
                      color.exteriorImage
                        ? 'Sample the colour from the exterior photo'
                        : 'Upload an exterior image first'
                    }
                    onClick={() =>
                      color.exteriorImage &&
                      setPicking({ index, asset: color.exteriorImage })
                    }
                  >
                    Pick from image
                  </button>
                </div>
                {!validHex ? (
                  <span className="field-error">Use a six-digit hex such as #1a1a1a</span>
                ) : (
                  <span className="field-hint">
                    Drives the colour chip in the app. Sampling from the photo keeps the chip and
                    the imagery consistent.
                  </span>
                )}
              </div>

              <div className="grid-2">
                <AssetField
                  label="Exterior image"
                  hint="The car in this colour."
                  prefix={`${assetPrefix}/colors`}
                  value={color.exteriorImage}
                  onChange={(exteriorImage) => update(index, { exteriorImage })}
                />
                <AssetField
                  label="Interior image"
                  hint="Cabin trim for this colour."
                  prefix={`${assetPrefix}/colors`}
                  value={color.interiorImage}
                  onChange={(interiorImage) => update(index, { interiorImage })}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {picking ? (
        <ColorPicker
          asset={picking.asset}
          value={value[picking.index]?.swatch ?? '#cccccc'}
          onPick={(hex) => update(picking.index, { swatch: hex })}
          onClose={() => setPicking(undefined)}
        />
      ) : null}
    </section>
  );
}
